import { EventEmitter } from "events";
import axios from "axios";
import { normalizeExpedienteId } from "../utils/expediente";

const CLIENT_CASE_TOKEN_PREFIX = "client_token_case_";

function getClientCaseTokenKey(caseId) {
  return `${CLIENT_CASE_TOKEN_PREFIX}${caseId}`;
}

function getCaseIdFromClientPath() {
  try {
    const pathname = window.location.pathname || "";
    if (pathname.startsWith("/client-sign")) {
      const params = new URLSearchParams(window.location.search || "");
      const id = params.get("id");
      const normalizedId = normalizeExpedienteId(id);
      return normalizedId || null;
    }

    const hash = window.location.hash || "";
    const isClientHashRoute =
      hash.startsWith("#/client-sign") || hash.startsWith("#//client-sign");

    if (!isClientHashRoute) {
      return null;
    }

    const hashQuery = hash.includes("?") ? hash.split("?")[1] : "";
    if (!hashQuery) {
      return null;
    }

    const hashParams = new URLSearchParams(hashQuery);
    const hashId = hashParams.get("id");
    const normalizedHashId = normalizeExpedienteId(hashId);
    return normalizedHashId || null;
  } catch (error) {
    return null;
  }
}

export function storeClientTokenForCase(caseId, token) {
  if (!caseId || !token) return;
  sessionStorage.setItem(getClientCaseTokenKey(String(caseId)), token);
}

export function clearClientTokenForCase(caseId) {
  if (!caseId) return;
  sessionStorage.removeItem(getClientCaseTokenKey(String(caseId)));
}

export function getStoredClientTokenForCase(caseId) {
  if (!caseId) return null;
  return sessionStorage.getItem(getClientCaseTokenKey(String(caseId)));
}

// Crear la instancia
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true, // habilita cookies (ej: HttpOnly JWT)
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Controlador de eventos globales
export const apiEvents = new EventEmitter();

// ======= SISTEMA DE DETECCIÓN DE SOLICITUDES REPETIDAS =======
const requestCounter = new Map(); // clave: firma del request, valor: contador
const MAX_REQUEST_RETRIES = 3;

// Endpoints de gestión de sesión que se repiten legítimamente — excluirlos del contador
const LOOP_DETECTION_EXCLUDED = [
  "/session/heartbeat",
  "/session/refresh",
  "/logout",
  "/getuser",
  "/messages/unread-count",
  "/general",
  "/reports/show",
  "/contactstable",
  "/contactsgraph",
  "/clientsmonthasoc",
  "/casesasoczone",
  "/graphcases",
  "/casesbystatus",
  "/kpistotals",
  "/contractssigned",
];

const REPORT_TRACE_ENDPOINTS = [
  "/general",
  "/reports/show",
  "/contactstable",
  "/contactsgraph",
  "/clientsmonthasoc",
  "/casesasoczone",
  "/graphcases",
  "/casesbystatus",
  "/kpistotals",
  "/contractssigned",
];

function isReportTraceRequest(url = "") {
  return REPORT_TRACE_ENDPOINTS.some((path) => String(url).includes(path));
}

function logReportRequest(config, status, error = null) {
  if (!config?._reportTraceStart) return;

  const duration = Math.round(performance.now() - config._reportTraceStart);
  const method = String(config.method || "GET").toUpperCase();
  const url = config.url || "";
  const message = `[InformeGeneral API] ${method} ${url} -> ${status} (${duration}ms)`;

  if (error || duration > 2500) {
    console.warn(message, error || "");
    return;
  }

  console.info(message);
}

// Genera una clave única basada en URL + método + data
function getRequestKey(config) {
  const dataString =
    typeof config.data === "object" ? JSON.stringify(config.data) : config.data;
  return `${config.method}:${config.url}:${dataString}`;
}

// ======= INTEGRAR SOCKET_ID GLOBAL SIN REPETIR CÓDIGO =======
export function attachEcho(echoInstance) {
  window.__ECHO__ = echoInstance;
  // console.log("🔌 Echo registrado en Axios");
}

// Interceptor REQUEST
api.interceptors.request.use(
  (config) => {
    if (
      typeof performance !== "undefined" &&
      isReportTraceRequest(config.url)
    ) {
      config._reportTraceStart = performance.now();
    }

    //  Agregar automáticamente X-Socket-Id si Echo está conectado
    if (window.__ECHO__) {
      const socketId = window.__ECHO__.socketId();
      if (socketId) {
        config.headers["X-Socket-Id"] = socketId;
        // console.log("🔗 Enviando X-Socket-Id:", socketId);
      }
    }

    //  Control de requests duplicados (excluye endpoints de sesión legítimamente repetidos)
    const isExcluded = LOOP_DETECTION_EXCLUDED.some((path) =>
      config.url?.includes(path),
    );

    if (!isExcluded) {
      const key = getRequestKey(config);
      const count = requestCounter.get(key) || 0;

      if (count >= MAX_REQUEST_RETRIES) {
        console.error(
          "⚠️ Múltiples solicitudes detectadas. Activando bloqueo de bucle.",
          config.url,
        );
        return Promise.reject(
          new Error(
            "Múltiples solicitudes detectadas. Activando bloqueo de bucle.",
          ),
        );
      }

      requestCounter.set(key, count + 1);
      // Limpiar después de 10 segundos para evitar crecimiento del mapa
      setTimeout(() => requestCounter.delete(key), 10000);
    }

    // Sesión cliente por expediente: priorizar token por caseId en /client-sign
    const currentCaseId = getCaseIdFromClientPath();
    const clientCaseToken = getStoredClientTokenForCase(currentCaseId);
    if (currentCaseId) {
      config.headers["X-Client-Case-Id"] = currentCaseId;
    }
    if (clientCaseToken) {
      config.headers["X-Client-Token"] = clientCaseToken;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Interceptor RESPONSE
api.interceptors.response.use(
  (response) => {
    logReportRequest(response.config, response.status);
    return response;
  },
  (error) => {
    logReportRequest(
      error?.config,
      error?.response?.status || "NETWORK_ERROR",
      error?.message,
    );

    if (error.response?.status === 401) {
      const skipUnauthorizedEvent = Boolean(
        error?.config?._skipUnauthorizedEvent,
      );
      if (!skipUnauthorizedEvent) {
        console.warn("No autorizado - emitiendo evento de logout");
        apiEvents.emit("unauthorized", {
          url: error?.config?.url,
          method: error?.config?.method,
          status: error?.response?.status,
          code: error?.response?.data?.code,
        });
      }
    }
    return Promise.reject(error);
  },
);

export default api;
